import type { FC, Ref } from "react";
import { Translate } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Box } from "@mui/material";
import type { System } from "../../../api/systems.types";
import {
  repointSystemConnectionsMutationOptions,
  systemSelectOptionsQueryOptions,
  type SystemConnectionRow,
} from "../../../api/systems";
import { ReplaceDialog } from "../../../shared/ReplaceDialog";
import { SystemIcon3 } from "../../../shared/icons";
import { SystemSelect } from "./SystemSelect";

type RepointConnectionsDialogProps = {
  onClose: () => void;
  systemId: string;
  systemName: string;
  selectedConnections: SystemConnectionRow[];
};

export const RepointConnectionsDialog: FC<RepointConnectionsDialogProps> = ({
  onClose,
  systemId,
  systemName,
  selectedConnections,
}) => {
  const repoint = useMutation(repointSystemConnectionsMutationOptions(systemId));
  const count = selectedConnections.length;

  return (
    <ReplaceDialog<System>
      onClose={onClose}
      selectStep={{
        title: "System.ReplaceAndArchive.Repoint.DialogHeader",
        fromField: {
          label: "System.ReplaceAndArchive.Repoint.CurrentSystemLabel",
          icon: <SystemIcon3 size={16} />,
          value: systemName,
        },
        targetField: {
          label: "System.ReplaceAndArchive.Repoint.MoveTo",
          renderSelect: ({ value, onChange, ref, isRequiredError }) => (
            <RepointTargetSelect
              value={value}
              onChange={onChange}
              inputRef={ref}
              isRequiredError={isRequiredError}
              currentSystemId={systemId}
            />
          ),
        },
        continueLabel: "System.ReplaceAndArchive.Repoint.ContinueButton",
      }}
      confirmStep={{
        renderDescription: ({ target }) => (
          <Translate
            translationKey="System.ReplaceAndArchive.Repoint.ConfirmMessage"
            values={{
              targetName: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {target?.name}
                </Box>
              ),
              count: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {count}
                </Box>
              ),
            }}
          />
        ),
        onConfirm: target => repoint.mutateAsync({ connections: selectedConnections, targetSystemId: target.id }),
        confirmLabel: "System.ReplaceAndArchive.Repoint.ConfirmButton",
      }}
    />
  );
};

type RepointTargetSelectProps = {
  currentSystemId: string;
  value: System | null;
  onChange: (system: System) => void;
  inputRef: Ref<unknown>;
  isRequiredError: boolean;
};

const RepointTargetSelect = ({
  currentSystemId,
  value,
  onChange,
  inputRef,
  isRequiredError,
}: RepointTargetSelectProps) => {
  const targetsQuery = useQuery(systemSelectOptionsQueryOptions());
  const targets = targetsQuery.data?.filter(({ id }) => id !== currentSystemId);
  return (
    <SystemSelect
      systems={targets}
      value={value}
      onChange={onChange}
      inputRef={inputRef}
      isRequiredError={isRequiredError}
      noOptionsText="System.ReplaceAndArchive.Repoint.NoActiveSystems"
    />
  );
};
